// Pure wrapper around provided exportDataGridToPdf behavior, but restores your original styling precisely

const { jsPDF } = window.jspdf;
import { exportDataGrid as exportDataGridToPdf } from "devextreme/pdf_exporter";
import { formatNumber } from "devextreme/localization";
import { addHeaderFooterToDoc } from './pdfHeaderFooter';
import { createCellCustomizer } from './cellCustomizerPdf';
import { PDF_STYLES } from './pdfStyles';

const MAX_SAMPLE_ROWS = 5000; // cap on rows measured per numeric column — bounds worst-case cost on large exports

/**
 * Reads each currently visible column's real rendered width (px) straight from the grid's
 * header DOM, keyed by column name/dataField. DevExtreme's own exporter only knows about the
 * `width` *option* (undefined for auto-sized columns, defaulting to a flat 150), which is why
 * exported column widths don't match what's actually on screen. Returns null when it can't
 * safely map cells to columns (e.g. a banded/multi-row header), so callers can fall back.
 */
function measureVisibleColumnWidthsPx(grid) {
    try {
        const rootEl = grid.element && grid.element();
        if (!rootEl) return null;

        const headerRows = rootEl.querySelectorAll('.dx-datagrid-headers .dx-header-row');
        if (headerRows.length !== 1) return null; // bail out for banded/multi-row headers

        const cells = Array.from(headerRows[0].children);
        const visibleGridColumns = grid.getVisibleColumns ? grid.getVisibleColumns() : [];
        if (!cells.length || cells.length !== visibleGridColumns.length) return null; // mismatch — don't risk a wrong mapping

        const widths = new Map();
        visibleGridColumns.forEach((col, i) => {
            const key = col.name || col.dataField;
            if (key) widths.set(key, cells[i].getBoundingClientRect().width);
        });
        return widths;
    } catch (err) {
        return null;
    }
}

/**
 * Fetches the full exported dataset (same filter/sort the grid is currently using, not just
 * the loaded page) and measures each numeric column's widest formatted value with jsPDF's own
 * text measurement, so that column can be given a width GUARANTEED to fit its content instead
 * of a heuristic guess. This doubles the data fetch for the export (DevExtreme's own exporter
 * does the same fetch internally, via a private controller API we don't have access to) — only
 * worth paying because a truncated number reads as broken in a way truncated text doesn't.
 * Returns an empty Map (never throws) on any failure, so callers can fall back gracefully.
 */
async function measureRequiredNumericColumnWidths({ grid, doc, candidateColumns }) {
    const widths = new Map();
    if (!candidateColumns.length) return widths;

    try {
        const dataSource = grid.getDataSource && grid.getDataSource();
        const store = dataSource && dataSource.store && dataSource.store();
        if (!store || !store.load) {
            // eslint-disable-next-line no-console
            console.log('[pdf-export DEBUG] no usable store/load — dataSource:', !!dataSource, 'store:', !!store);
            return widths;
        }

        const loadResult = await store.load({
            filter: dataSource.filter ? dataSource.filter() : undefined,
            sort: dataSource.sort ? dataSource.sort() : undefined,
        });
        const items = (Array.isArray(loadResult) ? loadResult : loadResult && loadResult.data) || [];
        const sample = items.length > MAX_SAMPLE_ROWS ? items.slice(0, MAX_SAMPLE_ROWS) : items;
        // eslint-disable-next-line no-console
        console.log('[pdf-export DEBUG] fetched rows for width measurement:', items.length, 'sample used:', sample.length);

        // `column.dataType` on the live grid's column config isn't reliably populated (it came
        // back empty even for columns that are clearly numeric) — fall back to the ACTUAL
        // fetched values as ground truth for "is this column numeric", regardless of how (or
        // whether) dataType was configured.
        const numericColumns = candidateColumns.filter((col) => {
            if (!col.dataField) return false;
            if (col.dataType === 'number') return true;
            const probeValues = items
                .slice(0, 20)
                .map((item) => item[col.dataField])
                .filter((v) => v !== null && v !== undefined);
            return probeValues.length > 0 && probeValues.every((v) => typeof v === 'number');
        });
        // eslint-disable-next-line no-console
        console.log('[pdf-export DEBUG] numeric columns inferred from data:', numericColumns.map((c) => c.name || c.dataField));

        // The grid's actual grouping columns (in group order). A group subtotal only sums the
        // rows within ONE group, not the whole dataset — assuming every row collapses into a
        // single bucket (the whole-dataset sum) is wildly pessimistic when there are many
        // groups, and over-reserves width for numeric columns at the expense of every other
        // column (this is what caused the regression: protecting numeric columns too generously
        // squeezed other columns harder than before).
        const allColumns = grid.getVisibleColumns ? grid.getVisibleColumns() : [];
        const groupByFields = allColumns
            .filter((c) => typeof c.groupIndex === 'number')
            .sort((a, b) => a.groupIndex - b.groupIndex)
            .map((c) => c.dataField)
            .filter(Boolean);
        const hasGrandTotal = (() => {
            try {
                const totalItems = grid.option('summary.totalItems');
                return Array.isArray(totalItems) && totalItems.length > 0;
            } catch (err) {
                return false;
            }
        })();
        // eslint-disable-next-line no-console
        console.log('[pdf-export DEBUG] groupByFields:', groupByFields, 'hasGrandTotal:', hasGrandTotal);

        const percentPattern = /[%]/;
        const formatForMeasurement = (col, isPercentFormat, value) => isPercentFormat
            ? `${value.toFixed(2)} %`
            : (col.format ? formatNumber(value, col.format) : String(value));

        numericColumns.forEach((col) => {
            const key = col.name || col.dataField;
            const isPercentFormat = typeof col.format === 'string' && percentPattern.test(col.format);
            let maxWidth = doc.getTextDimensions(String(col.caption || ''), { fontSize: PDF_STYLES.defaultFontSize }).w;
            sample.forEach((item) => {
                const value = col.dataField ? item[col.dataField] : undefined;
                if (value === null || value === undefined) return;
                // Mirror cellCustomizerPdf.js's own percentage formatting exactly, so the
                // measured width accounts for the same "XX.XX %" string that actually renders.
                const w = doc.getTextDimensions(formatForMeasurement(col, isPercentFormat, value), { fontSize: PDF_STYLES.defaultFontSize }).w;
                if (w > maxWidth) maxWidth = w;
            });

            if (!col.dataField) {
                widths.set(key, maxWidth);
                return;
            }

            // Group/subtotal rows show a SUM across the rows in ONE group — bound it with the
            // widest actual group's sum (bucketing the fetched rows the same way the grid
            // groups them), not the whole dataset.
            let maxGroupSum = 0;
            if (groupByFields.length) {
                const bucketSums = new Map();
                items.forEach((item) => {
                    const v = item[col.dataField];
                    if (typeof v !== 'number') return;
                    const bucketKey = groupByFields.map((f) => item[f]).join('|');
                    bucketSums.set(bucketKey, (bucketSums.get(bucketKey) || 0) + Math.abs(v));
                });
                bucketSums.forEach((sum) => { if (sum > maxGroupSum) maxGroupSum = sum; });
                if (maxGroupSum > 0) {
                    const w = doc.getTextDimensions(formatForMeasurement(col, isPercentFormat, maxGroupSum), { fontSize: PDF_STYLES.defaultFontSize }).w;
                    if (w > maxWidth) maxWidth = w;
                }
            }

            // The grand-total ('totalFooter') row, if the grid actually has one configured, is
            // the one case that legitimately needs the full-dataset sum — only pay for it when
            // that row will actually appear (or there's no grouping at all, so "the group" IS
            // the whole dataset).
            let sumOfAbs = 0;
            if (hasGrandTotal || !groupByFields.length) {
                sumOfAbs = items.reduce((acc, item) => {
                    const v = item[col.dataField];
                    return typeof v === 'number' ? acc + Math.abs(v) : acc;
                }, 0);
                if (sumOfAbs > 0) {
                    const w = doc.getTextDimensions(formatForMeasurement(col, isPercentFormat, sumOfAbs), { fontSize: PDF_STYLES.defaultFontSize }).w;
                    if (w > maxWidth) maxWidth = w;
                }
            }

            // eslint-disable-next-line no-console
            console.log(`[pdf-export DEBUG] column "${key}" (dataField: ${col.dataField}, format: ${col.format}) → maxGroupSum: ${maxGroupSum}, sumOfAbs: ${sumOfAbs}, final required width (pt): ${maxWidth}`);

            widths.set(key, maxWidth);
        });
    } catch (err) {
        // eslint-disable-next-line no-console
        console.log('[pdf-export DEBUG] measureRequiredNumericColumnWidths threw:', err);
        return new Map(); // fall back to DOM-measured widths on any failure (remote store quirks, permissions, etc.)
    }
    return widths;
}

// eslint-disable-next-line no-unused-vars
export async function exportAsPdf({ e, fileName, grid, finalColumnsForExport, formatCellValue, options, exportVisibilityMap }) {
    let lastPage = 1;
    const FIRST_PAGE_HEADER_HEIGHT = 40; // taller for letterhead
    const OTHER_PAGE_HEADER_HEIGHT = 20; // smaller for normal pages
    const orientationPoint = 8;


    let visibleColumns = finalColumnsForExport.filter((col) => col.visible);
    // visibleColumns can have move columns in one element attached to it, so we need to flatten it
    const flattenColumns = (cols) => {
        let flat = [];
        cols.forEach((col) => {
            if (col.columns && Array.isArray(col.columns)) {
                flat = flat.concat(flattenColumns(col.columns));
            } else {
                flat.push(col);
            }
        });
        return flat;
    };
    const visibleCount = flattenColumns(visibleColumns).length;


    const doc = new jsPDF({
        orientation: visibleCount > orientationPoint ? 'landscape' : 'portrait',
        unit: 'pt',
        format: 'a4',
        compress: true,
    });


    addHeaderFooterToDoc(doc, fileName, options);


    const LEFT_MARGIN = PDF_STYLES.leftMargin;
    const RIGHT_MARGIN = PDF_STYLES.rightMargin;
    const PX_TO_PT = 72 / 96; // CSS px (96/inch) → PDF points (72/inch), since doc uses unit: 'pt'
    const MIN_COLUMN_WIDTH_PT = 28; // preferred width below which a column becomes unreadable — a target, not a hard floor: fitting on one page always wins if the two conflict
    const MIN_FONT_SIZE_PT = 6; // preferred floor for readability — logged as a warning if breached, never enforced at the cost of page-fit
    const ABSOLUTE_MIN_SCALE = 0.05; // pure rendering safety net (avoid zero/negative widths or font sizes) — not a readability target
    const CONTENT_WIDTH_PADDING_PT = 8; // breathing room beyond the exact measured text width
    const TEXT_COLUMN_WIDTH_BOOST = 1.25; // deliberate edge for auto/text columns in the shared shrink/grow ratio, on request — numeric columns are sized exactly to their content (never truncate) and tend to dominate a proportionally-equal split, so text gets a modest head start
    const usablePageWidth = doc.internal.pageSize.getWidth() - LEFT_MARGIN - RIGHT_MARGIN;

    // Measure each column's true on-screen width now, before any export-time mutation below.
    const measuredWidths = measureVisibleColumnWidthsPx(grid);

    // Which columns are actually going to be exported, computed from the live grid BEFORE any
    // hiding below, filtering out command columns (e.g. the selection checkbox column from
    // `showCheckBoxesMode: 'always'`) the same way DevExtreme's own export logic does
    // (`_needColumnExporting` excludes `column.command`) — getVisibleColumns() alone doesn't.
    // Also filters out columns with no dataField — getVisibleColumns() can include a bogus entry
    // with dataField:"" that DevExtreme's exporter never actually renders as a column (see the
    // matching filter on finalVisibleColumns below for the full story).
    const liveVisibleColumns = (grid.getVisibleColumns ? grid.getVisibleColumns() : []).filter((col) => !col.command && col.dataField);
    const exportedColumns = exportVisibilityMap
        ? liveVisibleColumns.filter((col) => {
            const name = col.name || col.dataField;
            return exportVisibilityMap.has(name) ? exportVisibilityMap.get(name) : false;
        })
        : liveVisibleColumns;
    // Measure real content widths for numeric columns so they can be protected from the
    // shrink-to-fit ratio below — a truncated number reads as broken in a way truncated text
    // doesn't (see cellCustomizerPdf.js's wordWrapEnabled=false for numeric cells). Numeric-ness
    // is determined inside from actual fetched values, not `col.dataType` — that metadata came
    // back empty even for genuinely numeric columns, so passing every candidate column through
    // and letting the data itself decide is the reliable path.
    const requiredNumericWidths = await measureRequiredNumericColumnWidths({ grid, doc, candidateColumns: exportedColumns });
    // eslint-disable-next-line no-console
    console.log('[pdf-export DEBUG] requiredNumericWidths map:', Array.from(requiredNumericWidths.entries()));

    // DevExtreme 22.x pdf_exporter does not support customizeColumns.
    // Instead, temporarily hide excluded columns via columnOption() — a targeted per-column
    // update that does NOT trigger a server re-fetch (unlike replacing the full columns array).
    // beginUpdate/endUpdate suppresses any visual re-render during the mutation.
    const hiddenForExport = [];
    const needsEndUpdate = !!exportVisibilityMap;
    if (needsEndUpdate) grid.beginUpdate();

    let fontScale = 1;
    let loggedRowExportingCount = false; // TEMP DEBUG — remove once the column-count mismatch investigation below is resolved

    try {
        if (exportVisibilityMap) {
            const colCount = grid.columnCount ? grid.columnCount() : 0;
            for (let i = 0; i < colCount; i++) {
                const dataField = grid.columnOption(i, 'dataField');
                if (!dataField) continue; // skip band headers
                const name = grid.columnOption(i, 'name');
                const shouldExport = exportVisibilityMap.has(name)
                    ? exportVisibilityMap.get(name)
                    : false;
                if (!shouldExport && grid.columnOption(i, 'visible') !== false) {
                    hiddenForExport.push(i);
                    grid.columnOption(i, 'visible', false);
                }
            }
        }

        // Build the width array from the grid's ACTUAL post-hide visible column list (minus
        // command columns, and minus columns with no dataField — same convention as the hide/
        // reveal loop above, which already treats a missing dataField as "not a real leaf column,
        // skip it"). Confirmed via onRowExporting + a real export: grid.getVisibleColumns() can
        // return a bogus entry with dataField:"" and no real caption (defaults to the generic type
        // name, e.g. "String") that DevExtreme's own PDF exporter never actually renders as a
        // column. Without this filter, our columnWidths array has one more entry than DevExtreme's
        // real internal column list, so its positional length check silently drops the LAST
        // entry's width from ever being applied — that width still counted toward our own
        // page-fit sum, so the total looked correct on our side, but the real table came up short
        // by exactly that column's width, leaving a gap before the right margin.
        const finalVisibleColumns = (grid.getVisibleColumns ? grid.getVisibleColumns() : []).filter((col) => !col.command && col.dataField);

        let columnWidths;
        if (requiredNumericWidths.size > 0 || measuredWidths) {
            const entries = finalVisibleColumns.map((col) => {
                const key = col.name || col.dataField;
                // requiredNumericWidths.has(key) alone is the source of truth here — it was
                // already populated from actual data values, not `col.dataType` (which came back
                // unreliable/empty even for genuinely numeric columns).
                if (requiredNumericWidths.has(key)) {
                    return { widthPt: requiredNumericWidths.get(key) + CONTENT_WIDTH_PADDING_PT, protectedWidth: true };
                }
                const measuredPx = measuredWidths ? measuredWidths.get(key) : null;
                if (typeof measuredPx === 'number' && measuredPx > 0) {
                    return { widthPt: measuredPx * PX_TO_PT * TEXT_COLUMN_WIDTH_BOOST, protectedWidth: false };
                }
                // No reliable on-screen measurement for this column (e.g. banded/multi-row
                // headers — see measureVisibleColumnWidthsPx). Falling back to a flat
                // MIN_COLUMN_WIDTH_PT for every such column gave them all the same tiny "natural"
                // width regardless of content, which then fed into the proportional shrink/grow
                // ratio below and made text columns look uniformly squeezed next to numeric
                // columns sized from real content. Measuring the column's own header caption
                // (the same jsPDF technique already used for numeric columns' required widths)
                // gives a natural width that at least reflects that specific column's content,
                // instead of an arbitrary constant shared by every unmeasured column.
                const captionWidth = doc.getTextDimensions(String(col.caption || ''), { fontSize: PDF_STYLES.defaultFontSize }).w;
                const widthPt = Math.max((captionWidth + CONTENT_WIDTH_PADDING_PT) * TEXT_COLUMN_WIDTH_BOOST, MIN_COLUMN_WIDTH_PT);
                return { widthPt, protectedWidth: false };
            });

            const protectedTotal = entries.filter((en) => en.protectedWidth).reduce((a, en) => a + en.widthPt, 0);
            const autoEntries = entries.filter((en) => !en.protectedWidth);
            const autoTotal = autoEntries.reduce((a, en) => a + en.widthPt, 0);
            const totalNatural = protectedTotal + autoTotal;

            // Fitting on one page is the one hard, non-negotiable requirement (no exceptions,
            // even at the cost of a very small font). Earlier this session, auto (text) columns
            // were pinned to a bare 28pt floor first and protected (numeric) columns absorbed
            // whatever was left — mathematically safe, but visually lopsided: numeric columns
            // ended up dominating the page while every text column was crushed to the same
            // narrow floor regardless of its own content. When the combined natural/required
            // width of every column doesn't fit, numeric and text columns now shrink TOGETHER by
            // the SAME ratio, relative to each one's own natural width, so both groups give up
            // roughly the same proportion of what they actually need rather than one absorbing
            // all of it.
            let protectedRatio = 1;
            let autoRatio = 1;
            if (totalNatural > usablePageWidth) {
                const sharedRatio = totalNatural > 0 ? usablePageWidth / totalNatural : 1;
                protectedRatio = sharedRatio;
                autoRatio = sharedRatio;
            } else {
                // Comfortably fits at natural size — leftover page width should still be consumed
                // so the table reaches the right margin instead of stopping short. Auto (text)
                // columns get first claim on it (their width is already sized from real/estimated
                // content, so growing them proportionally still looks reasonable). If there are NO
                // auto columns at all (a grid that's entirely, or almost entirely, numeric), there's
                // nothing else to absorb the leftover — so protected (numeric) columns grow instead,
                // on request, rather than leaving a permanent blank gap on the right for that shape
                // of grid.
                const remainingForAuto = usablePageWidth - protectedTotal;
                if (autoTotal > 0) {
                    autoRatio = remainingForAuto / autoTotal;
                } else {
                    protectedRatio = protectedTotal > 0 ? usablePageWidth / protectedTotal : 1;
                }
            }

            // Required numeric widths were measured at scale 1, so a protected column only keeps
            // fitting its text if its width shrinks by the SAME ratio as the font — fontScale
            // tracks protectedRatio EXACTLY whenever protectedRatio < 1 (shrinking), with only a
            // tiny rendering-safety floor (not a readability floor: page-fit must hold
            // unconditionally, per instruction). When protectedRatio >= 1 (the grow-to-fill-the-
            // gap case just above), fontScale stays at 1 — a wider numeric column doesn't need a
            // bigger font, the extra room is just breathing space, not a truncation concern — so
            // protectedWidthRatio (used for the actual column width below) is allowed to diverge
            // from fontScale in that direction only. It must NEVER diverge on the shrink side, or
            // a floored fontScale higher than the raw shrink ratio would re-open truncation.
            fontScale = protectedRatio < 1 ? Math.max(protectedRatio, ABSOLUTE_MIN_SCALE) : 1;
            const protectedWidthRatio = protectedRatio < 1 ? fontScale : protectedRatio;
            if (fontScale < MIN_FONT_SIZE_PT / PDF_STYLES.defaultFontSize) {
                // eslint-disable-next-line no-console
                console.log('[pdf-export DEBUG] fontScale dropped below the preferred readability floor to guarantee one-page fit:', fontScale, '→', (fontScale * PDF_STYLES.defaultFontSize).toFixed(2), 'pt');
            }

            columnWidths = entries.map((en) => en.protectedWidth
                ? Math.max(en.widthPt * protectedWidthRatio, ABSOLUTE_MIN_SCALE)
                : Math.max(en.widthPt * autoRatio, ABSOLUTE_MIN_SCALE));

            // eslint-disable-next-line no-console
            console.log('[pdf-export DEBUG] final columns/widths:', finalVisibleColumns.map((c, i) => ({
                key: c.name || c.dataField,
                dataField: c.dataField,
                dataType: c.dataType,
                command: c.command,
                isBand: c.isBand,
                groupIndex: c.groupIndex,
                showWhenGrouped: c.showWhenGrouped,
                colspan: c.colspan,
                protectedWidth: entries[i].protectedWidth,
                widthPt: columnWidths[i],
            })));
            // eslint-disable-next-line no-console
            console.log('[pdf-export DEBUG] usablePageWidth:', usablePageWidth, 'protectedTotal:', protectedTotal, 'autoTotal:', autoTotal, 'protectedRatio:', protectedRatio, 'protectedWidthRatio:', protectedWidthRatio, 'autoRatio:', autoRatio, 'fontScale:', fontScale, 'sum of columnWidths:', columnWidths.reduce((a, w) => a + w, 0));
        } else {
            // eslint-disable-next-line no-console
            console.log('[pdf-export DEBUG] columnWidths left unset — neither requiredNumericWidths nor measuredWidths were available');
        }

        const cellCustomizer = createCellCustomizer({ formatCellValue, fontScale });

        await exportDataGridToPdf({
            jsPDFDocument: doc,
            component: e.component,
            // x MUST be 0, not LEFT_MARGIN: DevExtreme's calculateCoordinates() computes the
            // table's real starting x as `topLeft.x + margin.left` (row_utils.js) — the two are
            // ADDED, not alternatives. Setting both to LEFT_MARGIN double-counted it, so the
            // table's actual right edge landed LEFT_MARGIN points further right than
            // usablePageWidth (computed below) assumed. The page-split boundary check
            // (rows_splitting.js) only ever subtracts margin.right and never looks at topLeft.x,
            // so nothing on the right side compensated — any export whose columnWidths summed to
            // exactly usablePageWidth (which fix #13's "grow to fill leftover space" made the
            // normal case, not the exception) would overflow the last column onto a spurious extra
            // page by exactly LEFT_MARGIN points. Margin.left alone now supplies the entire left
            // offset.
            topLeft: { x: 0, y: 2 },
            // Must match the margins usablePageWidth was computed with above — DevExtreme's own
            // horizontal page-split check compares the table's right edge against
            // (pageWidth - margin.right), and defaults to a 40pt margin on every side when this
            // isn't set, which doesn't match our 4pt PDF_STYLES margins. Top/bottom kept at the
            // library's existing default (40) since vertical pagination isn't part of this fix.
            margin: { top: 40, bottom: 40, left: LEFT_MARGIN, right: RIGHT_MARGIN },
            columnWidths,
            customizeCell: cellCustomizer,
            // TEMP DEBUG — onRowExporting is the only hook that exposes DevExtreme's REAL internal
            // per-row cell count, i.e. how many columns it actually rendered, independent of what
            // we computed columnWidths for. Comparing this against finalVisibleColumns.length (see
            // the "final columns/widths" log above) tells us definitively whether there's a
            // positional/count mismatch behind the "table doesn't reach the right margin" reports —
            // logged only once (first row) to avoid spamming the console on large exports.
            onRowExporting(args) {
                if (loggedRowExportingCount) return;
                loggedRowExportingCount = true;
                // eslint-disable-next-line no-console
                console.log('[pdf-export DEBUG] onRowExporting — DevExtreme\'s actual rendered cell count for the first row:', args.rowCells ? args.rowCells.length : 'n/a', 'vs finalVisibleColumns.length:', finalVisibleColumns.length);
            },
            customDrawCell({ rect, gridCell }) {
                if (!rect) return;


                const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
                const headerOffset = currentPage === 1 ? FIRST_PAGE_HEADER_HEIGHT : OTHER_PAGE_HEADER_HEIGHT;


                if (currentPage !== lastPage) {
                    lastPage = currentPage;
                    rect.y += headerOffset;
                }

                // Hand-drawn dotted bottom border for detail (data) rows — DevExtreme's own
                // border drawing only supports solid lines (no dash/style option on pdfCell at
                // all), so cellCustomizerPdf.js suppresses the default solid bottom border for
                // 'data' rows (drawBottomBorder = false) and this draws the dotted replacement
                // directly via jsPDF's own line-drawing API. Line-dash state is reset immediately
                // after drawing so it doesn't leak into DevExtreme's own (solid) border draws for
                // subsequent cells, which never reset it themselves.
                if (gridCell && gridCell.rowType === 'data') {
                    doc.setDrawColor(PDF_STYLES.detailRowDividerColor);
                    doc.setLineWidth(PDF_STYLES.detailRowDividerWidth);
                    doc.setLineCap('round'); // near-zero dash + round cap renders as dots, not dashes
                    doc.setLineDashPattern(PDF_STYLES.detailRowDividerDash, 0);
                    doc.line(rect.x, rect.y + rect.h, rect.x + rect.w, rect.y + rect.h);
                    // Reset both immediately — neither is reset by DevExtreme's own border
                    // drawing (draw_utils.js only resets color/width, never dash or cap), so
                    // leaving either set would leak into every subsequent solid border it draws.
                    doc.setLineDashPattern([], 0);
                    doc.setLineCap('butt');
                }
            },
        });
    } finally {
        // Restore temporarily hidden columns and release visual update batching
        hiddenForExport.forEach((i) => grid.columnOption(i, 'visible', true));
        if (needsEndUpdate) grid.endUpdate();
    }


    doc.save(`${fileName}.pdf`);
}
